"use client";

import { useState, useMemo } from "react";
import { Search, Phone, MapPin } from "lucide-react";
import { Input } from "@/app/_module/components/ui/input";
import { Button } from "@/app/_module/components/ui/button";
import { Card } from "@/app/_module/components/ui/card";
import { Badge } from "@/app/_module/components/ui/badge";
import { ScrollArea } from "@/app/_module/components/ui/scroll-area";

interface PhoneNumber {
  id: string;
  number: string;
  areaCode: string;
  state: string;
  city?: string;
  isActive: boolean;
}

interface PhoneNumberSelectorProps {
  phoneNumbers: PhoneNumber[];
  onSelect: (phoneNumber: PhoneNumber) => void;
  isLoading?: boolean;
}

export function PhoneNumberSelector({ 
  phoneNumbers, 
  onSelect,
  isLoading = false 
}: PhoneNumberSelectorProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredNumbers = useMemo(() => {
    if (!searchTerm.trim()) {
      return phoneNumbers;
    }

    const search = searchTerm.toLowerCase();
    return phoneNumbers.filter((phone) => {
      return (
        phone.number.toLowerCase().includes(search) ||
        phone.areaCode.toLowerCase().includes(search) ||
        phone.state.toLowerCase().includes(search) ||
        phone.city?.toLowerCase().includes(search)
      );
    });
  }, [phoneNumbers, searchTerm]);

  // Group by area code
  const groupedByAreaCode = useMemo(() => {
    const groups: Record<string, PhoneNumber[]> = {};
    
    filteredNumbers.forEach((phone) => {
      const key = `${phone.areaCode} - ${phone.state}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(phone);
    });

    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredNumbers]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400 animate-pulse" />
          <Input
            disabled
            placeholder="Loading phone numbers..."
            className="pl-10"
          />
        </div>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (phoneNumbers.length === 0) {
    return (
      <div className="text-center py-12">
        <Phone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No phone numbers available
        </h3>
        <p className="text-gray-500">
          There are currently no phone numbers available for assignment.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search by number, area code, state, or city..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Results */}
      {filteredNumbers.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">
            No phone numbers match your search criteria.
          </p>
          <Button
            variant="link"
            onClick={() => setSearchTerm("")}
            className="mt-2"
          >
            Clear search
          </Button>
        </div>
      ) : (
        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-4">
            {groupedByAreaCode.map(([areaCodeGroup, numbers]) => (
              <div key={areaCodeGroup} className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <MapPin className="h-4 w-4" />
                  <span>{areaCodeGroup}</span>
                  <Badge variant="secondary" className="ml-auto">
                    {numbers.length} {numbers.length === 1 ? 'number' : 'numbers'}
                  </Badge>
                </div>
                
                <div className="space-y-2 ml-6">
                  {numbers.map((phone) => (
                    <Card
                      key={phone.id}
                      className="p-3 hover:bg-gray-50 transition-colors cursor-pointer border border-gray-200"
                      onClick={() => onSelect(phone)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Phone className="h-5 w-5 text-blue-600" />
                          <div>
                            <p className="font-medium text-gray-900">
                              {phone.number}
                            </p>
                            {phone.city && (
                              <p className="text-sm text-gray-500">
                                {phone.city}, {phone.state}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelect(phone);
                          }}
                        >
                          Select
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Summary */}
      <div className="text-sm text-gray-500 text-center pt-2 border-t">
        {filteredNumbers.length === phoneNumbers.length ? (
          <span>Showing all {phoneNumbers.length} available phone numbers</span>
        ) : (
          <span>
            Showing {filteredNumbers.length} of {phoneNumbers.length} phone numbers
          </span>
        )}
      </div>
    </div>
  );
}
